import {
  MAKESHIFT_BENCH_ART,
  MAKESHIFT_BENCH_CLOSE_UP,
  worktableArtPaths,
} from "../views/machine-sprites/worktable-art";
import { BOARD_FACE_TEXTURE_ASSETS } from "../views/material-sprites/boardFaceTextures";
import { SHEET_FACE_TEXTURE_ASSETS } from "../views/material-sprites/sheetFaceTextures";
import { Assets } from "pixi.js";
import { CONSUMABLE_TYPES, ConsumableId } from "../game/Consumable";
import { TOOL_TYPES, ToolId } from "../game/Tool";
import { UPGRADE_TYPES, UpgradeId } from "../game/Upgrade";
import {
  BAR_CLAMP_ICON,
  BROOM_ICON,
  IDS_WITHOUT_ICON_ART,
  SHOP_VAC_ICON,
  consumableIconSrc,
  toolIconSrc,
  upgradeIconSrc,
} from "./uiImages";

/**
 * The tool icons double as the shop-floor sprite for a tool lying loose
 * (see ToolItemSprite), so PIXI needs them alongside the machine art.
 */
const TOOL_ITEM_ASSETS = (Object.keys(TOOL_TYPES) as ToolId[])
  .filter((toolId) => !IDS_WITHOUT_ICON_ART.tools.includes(toolId))
  .map(toolIconSrc);

/**
 * The supply icons do the same on the store's shelves — a racking bay
 * with no world-size art shows its product's own icon
 * (StoreMerchandiseView), so PIXI needs those too.
 */
const SUPPLY_SHELF_ASSETS = [
  ...(Object.keys(CONSUMABLE_TYPES) as ConsumableId[])
    .filter((id) => !IDS_WITHOUT_ICON_ART.consumables.includes(id))
    .map(consumableIconSrc),
  ...(Object.keys(UPGRADE_TYPES) as UpgradeId[])
    .filter((id) => !IDS_WITHOUT_ICON_ART.upgrades.includes(id))
    .map(upgradeIconSrc),
  BAR_CLAMP_ICON,
  SHOP_VAC_ICON,
  BROOM_ICON,
];

/**
 * Pixel art, as opposed to the smooth machine art: these have to sample
 * nearest-neighbor or the shop's fit-to-column upscale blurs them.
 */
const PIXEL_ART_ASSETS = [...TOOL_ITEM_ASSETS, ...SUPPLY_SHELF_ASSETS];

// List of all texture assets used in the game
export const TEXTURE_ASSETS = [
  "/images/asphalt.png",
  "/images/grass.png",
  "/images/bandsaw-14-lower.png",
  "/images/bandsaw-14-fence.png",
  "/images/bandsaw-14-upper.png",
  "/images/benchtop-jointer.png",
  "/images/concrete-floor-2-big.png",
  "/images/garbage-can.png",
  "/images/jobsite-table-saw-table.png",
  "/images/jobsite-table-saw-fence.png",
  "/images/lunchbox-planer-bottom.png",
  "/images/lunchbox-planer-top.png",
  "/images/lunchbox-planer-screws.png",
  "/images/miter-saw-stationary-base.png",
  "/images/miter-saw-rotating-base.png",
  "/images/miter-saw-top.png",
  "/images/operator-position.png",
  "/images/pickup-truck.png",
  "/images/workspace.png",
  MAKESHIFT_BENCH_ART,
  // The bench view's close-up copy of the same art (see benchCloseUpArt)
  MAKESHIFT_BENCH_CLOSE_UP,
  "/images/person.png",
  // Worktable tops, shadows, and their close-up copies — three layers per
  // table, and only for the tables that have art (see worktable-art.ts)
  ...worktableArtPaths(),
  // The sheet goods' seamless face tiles (see sheetFaceTextures.ts) and
  // the boards' full-plank scans (see boardFaceTextures.ts)
  ...SHEET_FACE_TEXTURE_ASSETS,
  ...BOARD_FACE_TEXTURE_ASSETS,
  ...PIXEL_ART_ASSETS,
];

/** Every photograph a piece of wood is drawn from. */
const MATERIAL_TEXTURE_ASSETS = [
  ...SHEET_FACE_TEXTURE_ASSETS,
  ...BOARD_FACE_TEXTURE_ASSETS,
];

/**
 * How the wood's sources sample. Split out from loadAssets because the
 * swatch page (src/dev) loads the same art on its own, and a swatch
 * sampled differently from the shop would be auditing the wrong thing.
 */
function configureMaterialSources(): void {
  // The sheet tiles repeat (a piece's texture window can sit anywhere on
  // the source sheet) and render minified five-fold at shop scale, so
  // they need wrap addressing and mipmaps where the machine art needs
  // neither.
  for (const path of SHEET_FACE_TEXTURE_ASSETS) {
    const source = Assets.get(path).source;
    source.autoGenerateMipmaps = true;
    source.style.addressMode = "repeat";
    source.style.update();
  }

  // The board scans want the mipmaps but not the wrap: a board's window
  // never leaves its scan, and a photographed plank has real edges that
  // must not bleed around.
  for (const path of BOARD_FACE_TEXTURE_ASSETS) {
    const source = Assets.get(path).source;
    source.autoGenerateMipmaps = true;
  }
}

/**
 * Load all game assets before starting the application
 */
export async function loadAssets(): Promise<void> {
  // Add all assets to the Assets cache with their paths as keys
  TEXTURE_ASSETS.forEach((path) => {
    Assets.add({ alias: path, src: path });
  });

  // Load all assets
  await Assets.load(TEXTURE_ASSETS);

  for (const path of PIXEL_ART_ASSETS) {
    Assets.get(path).source.scaleMode = "nearest";
  }

  configureMaterialSources();
}

/**
 * Just the wood — the board scans and the sheet tiles, sampled exactly
 * as the shop samples them. For pages that draw materials and nothing
 * else (see src/dev/swatches.ts), so they don't wait on machine art.
 */
export async function loadMaterialTextures(): Promise<void> {
  MATERIAL_TEXTURE_ASSETS.forEach((path) => {
    Assets.add({ alias: path, src: path });
  });
  await Assets.load(MATERIAL_TEXTURE_ASSETS);
  configureMaterialSources();
}
